/**
 * ShareEmailForm — pure presentational form for "share via email" surfaces.
 *
 * Extracted from ``ShareDialog`` (Jun 16, 2026) so the tabbed
 * ``ShareDashboardSheet`` can reuse the SAME email/message/validation
 * pipeline without nesting two dialogs. Single source of truth for:
 *
 *   - RFC 5322-minimal email regex (delegated from caller)
 *   - Message textarea + character counter (soft warn at 400, hard 500)
 *   - Inline ``role="alert"`` error band for parent mutation failures
 *   - Lock-on-submit chrome (Cancel disabled, Send swaps to spinner)
 *   - First-focus on email input via ``focusOnMount`` (RAF + ref)
 *   - Form reset on every closed→open transition keyed by ``resetKey``
 *
 * The component renders the FORM + the action buttons (Cancel / Send).
 * It does NOT render a Dialog wrapper — the caller decides whether to
 * mount it inside a Dialog (``<ShareDialog>``) or inline inside a tab
 * panel (``<ShareDashboardSheet>``'s Email tab).
 */

import * as React from "react";
import { Loader2, Send } from "lucide-react";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";

import { Button } from "../primitives/button";
import { cn } from "../lib/cn";

const MESSAGE_MAX = 500;
const MESSAGE_WARN_AT = 400;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export interface ShareEmailFormSubmitInput {
  /** Trimmed, validated email — always non-empty. */
  readonly recipient_email: string;
  /** Trimmed message, or undefined if empty/whitespace. */
  readonly message?: string;
}

export interface ShareEmailFormProps {
  /** True while the parent mutation is in-flight. Locks inputs + buttons. */
  readonly isSubmitting: boolean;
  /** Inline error from the parent mutation. */
  readonly error?: string | null;
  /** Fires when the user clicks "Invia" with a valid email. */
  readonly onSubmit: (input: ShareEmailFormSubmitInput) => void;
  /** Called when the user clicks "Annulla". */
  readonly onCancel: () => void;
  /**
   * Bump this key to reset the form (clears email + message + touched).
   * Mirrors ``open`` flipping to true in the legacy ShareDialog wrapper.
   */
  readonly resetKey?: unknown;
  /** When true the email input grabs focus on mount via RAF. */
  readonly focusOnMount?: boolean;
  /** Unique id prefix for input/label associations (avoids id collisions
   *  when multiple instances live on the page). */
  readonly idPrefix?: string;
}

function isValidEmail(value: string): boolean {
  return EMAIL_REGEX.test(value.trim());
}

export const ShareEmailForm: React.FC<ShareEmailFormProps> = ({
  isSubmitting,
  error,
  onSubmit,
  onCancel,
  resetKey,
  focusOnMount,
  idPrefix = "share-email-form",
}) => {
  const { i18n } = useLingui();
  const emailInputId = `${idPrefix}-email`;
  const messageInputId = `${idPrefix}-message`;
  const errorId = `${idPrefix}-email-error`;

  const [email, setEmail] = React.useState("");
  const [message, setMessage] = React.useState("");
  const [emailTouched, setEmailTouched] = React.useState(false);
  const emailInputRef = React.useRef<HTMLInputElement | null>(null);

  // Reset on every resetKey change so the parent (Dialog open→close
  // cycle, tab switch, etc.) can wipe form state without unmounting us.
  React.useEffect(() => {
    setEmail("");
    setMessage("");
    setEmailTouched(false);
    if (focusOnMount) {
      const id = requestAnimationFrame(() => emailInputRef.current?.focus());
      return () => cancelAnimationFrame(id);
    }
    return undefined;
  }, [resetKey, focusOnMount]);

  const trimmedEmail = email.trim();
  const emailValid = isValidEmail(email);
  const emailEmpty = trimmedEmail.length === 0;
  const showEmailError = emailTouched && !emailEmpty && !emailValid;
  const canSubmit = emailValid && !isSubmitting;

  const messageLen = message.length;
  const messageWarn = messageLen > MESSAGE_WARN_AT && messageLen <= MESSAGE_MAX;

  const handleSubmit = React.useCallback(() => {
    if (!canSubmit) {
      setEmailTouched(true);
      return;
    }
    const trimmedMessage = message.trim();
    onSubmit({
      recipient_email: trimmedEmail,
      message: trimmedMessage.length > 0 ? trimmedMessage : undefined,
    });
  }, [canSubmit, message, onSubmit, trimmedEmail]);

  return (
    <>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          handleSubmit();
        }}
        className="flex flex-col gap-4 py-2"
      >
        <div className="flex flex-col gap-1.5">
          <label
            htmlFor={emailInputId}
            className="font-medium text-[11px] text-ink-3 uppercase tracking-[0.05em]"
          >
            {i18n._(msg`Email destinatario`)}
          </label>
          <input
            id={emailInputId}
            ref={emailInputRef}
            type="email"
            autoComplete="email"
            placeholder={i18n._(msg`nome@esempio.com`)}
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            onBlur={() => setEmailTouched(true)}
            disabled={isSubmitting}
            required
            aria-invalid={showEmailError || undefined}
            aria-describedby={showEmailError ? errorId : undefined}
            className={cn(
              "w-full rounded-md border bg-white/70 px-3 py-2 text-[13px] text-ink-1 outline-none transition-colors",
              "placeholder:text-ink-3/70",
              "focus:border-ando-orange/60 focus:ring-2 focus:ring-ando-orange/20",
              "disabled:cursor-not-allowed disabled:opacity-60",
              showEmailError ? "border-rose-300" : "border-hairline",
            )}
          />
          {showEmailError ? (
            <p id={errorId} className="text-[11.5px] text-rose-600">
              {i18n._(msg`Inserisci un indirizzo email valido.`)}
            </p>
          ) : null}
        </div>

        <div className="flex flex-col gap-1.5">
          <label
            htmlFor={messageInputId}
            className="font-medium text-[11px] text-ink-3 uppercase tracking-[0.05em]"
          >
            {i18n._(msg`Messaggio`)}{" "}
            <span className="text-ink-3/70 lowercase">{i18n._(msg`(opzionale)`)}</span>
          </label>
          <textarea
            id={messageInputId}
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            disabled={isSubmitting}
            maxLength={MESSAGE_MAX}
            rows={3}
            placeholder={i18n._(msg`Aggiungi una nota personale…`)}
            className={cn(
              "w-full resize-none rounded-md border bg-white/70 px-3 py-2 text-[12.5px] text-ink-1 outline-none transition-colors",
              "placeholder:text-ink-3/70",
              "focus:border-ando-orange/60 focus:ring-2 focus:ring-ando-orange/20",
              "disabled:cursor-not-allowed disabled:opacity-60",
              messageWarn ? "border-amber-300" : "border-hairline",
            )}
          />
          <p
            className={cn(
              "text-[10.5px]",
              messageWarn ? "text-amber-600" : "text-ink-3",
            )}
          >
            {messageLen}/{MESSAGE_MAX}
            {messageWarn ? ` · ${i18n._(msg`si avvicina al limite`)}` : null}
          </p>
        </div>

        {error ? (
          <p
            role="alert"
            className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-[12px] text-rose-700"
          >
            {error}
          </p>
        ) : null}
      </form>

      <div className="flex justify-end gap-2">
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          {i18n._(msg`Annulla`)}
        </Button>
        <Button
          type="button"
          variant="primary"
          size="sm"
          onClick={handleSubmit}
          disabled={!canSubmit}
          aria-label={i18n._(msg`Invia`)}
        >
          {isSubmitting ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
          ) : (
            <Send className="h-3.5 w-3.5" aria-hidden="true" />
          )}
          {isSubmitting ? i18n._(msg`Invio…`) : i18n._(msg`Invia`)}
        </Button>
      </div>
    </>
  );
};

ShareEmailForm.displayName = "ShareEmailForm";
